/**
 * JSON-LD structured-data builders for the SEO component.
 * Static schemas are plain constants; anything that depends on the current
 * page is a small pure function. Content is unchanged from the original
 * inline definitions, only relocated.
 */

export type PageType = 'home' | 'about' | 'projects' | 'contact' | 'chat' | 'blog' | 'project-details';

export interface ProjectData {
  title: string;
  description: string;
  category: string;
  tags: string[];
  slug: string;
  image?: string;
  completedDate?: string;
  demoUrl?: string;
  codeUrl?: string;
}

export interface ArticleData {
  title: string;
  description: string;
  publishDate: string;
  modifiedDate?: string;
  author: string;
  keywords: string;
  readingTime: string;
  slug: string;
  image?: string;
}

const PERSON_SAME_AS = [
  'https://github.com/tkkalkhaled',
  'https://www.linkedin.com/in/talal-alkhaled-a0779b321/',
];

export const websiteSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: 'Talal Alkhaled - Computer Engineering Portfolio',
  alternateName: 'Elite Engineering Solutions by Talal Al-Khaled',
  url: 'https://talkhaled.com/',
  description:
    'Comprehensive portfolio showcasing advanced computer engineering projects, AI applications, web development, mobile apps, and innovative technical solutions.',
  inLanguage: 'en-US',
  author: { '@type': 'Person', name: 'Talal Alkhaled' },
  publisher: { '@type': 'Person', name: 'Talal Alkhaled' },
  copyrightYear: 2024,
  copyrightHolder: { '@type': 'Person', name: 'Talal Alkhaled' },
};

export const organizationSchema = {
  '@context': 'https://schema.org',
  '@type': ['Person', 'Organization'],
  name: 'Talal Alkhaled',
  alternateName: ['Talal Al-Khaled', 'Talal Al Khaled', 'طلال الخالد'],
  url: 'https://talkhaled.com/',
  logo: 'https://talkhaled.com/optimized/Icon.webp',
  image: 'https://talkhaled.com/optimized/Talal-md.webp',
  description:
    'Computer Engineer from Michigan State University specializing in AI integration, web development, mobile applications, hardware design, and enterprise security implementations. Developer of 11+ innovative projects including AI-powered applications, neural network accelerators, and high-performance web systems.',
  founder: {
    '@type': 'Person',
    name: 'Talal Alkhaled',
    jobTitle: 'Computer Engineer & Full-Stack Developer',
    affiliation: {
      '@type': 'EducationalOrganization',
      name: 'Michigan State University',
      url: 'https://msu.edu/',
    },
    knowsAbout: [
      'Computer Engineering',
      'Artificial Intelligence',
      'Web Development',
      'Mobile App Development',
      'Hardware Design',
      'Web Security',
      'Performance Optimization',
      'React Development',
      'Flutter Development',
      'OpenAI Integration',
      'Machine Learning',
      'FPGA Design',
    ],
    hasOccupation: {
      '@type': 'Occupation',
      name: 'Computer Engineer & Developer',
      occupationLocation: { '@type': 'Place', name: 'Kuwait' },
      skills:
        'React, Flutter, Python, TypeScript, AI Integration, Web Security, Hardware Design, Performance Optimization',
    },
  },
  contactPoint: {
    '@type': 'ContactPoint',
    contactType: 'Business Inquiries',
    url: 'https://talkhaled.com/contact',
    email: 'contact@talkhaled.com',
    telephone: '+96597499111',
    availableLanguage: ['English', 'Arabic'],
  },
  sameAs: [...PERSON_SAME_AS, 'https://www.instagram.com/talalal5aled'],
  address: {
    '@type': 'PostalAddress',
    addressLocality: 'Kuwait City',
    addressCountry: 'KW',
  },
  serviceArea: { '@type': 'Place', name: 'Worldwide (Remote Services Available)' },
  makesOffer: {
    '@type': 'Offer',
    itemOffered: {
      '@type': 'Service',
      name: 'Full-Stack Development Services',
      description:
        'Web development, mobile app development, AI integration, security optimization, and hardware design services',
    },
  },
};

/** Home page FAQ rich results. */
export const faqSchema = {
  '@context': 'https://schema.org',
  '@type': 'FAQPage',
  mainEntity: [
    {
      '@type': 'Question',
      name: 'What programming languages and technologies does Talal Alkhaled specialize in?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Talal Alkhaled is expert in Python, JavaScript, TypeScript, C++, Dart (Flutter), SQL, Verilog, and VHDL. He specializes in React, Node.js, Flutter, OpenAI API integration, machine learning frameworks (spaCy, NLTK, Transformers), FPGA development, and modern web security implementations. His technical stack includes advanced AI integration, hardware design, and enterprise-grade security practices.',
      },
    },
    {
      '@type': 'Question',
      name: "What is Talal Alkhaled's educational background and current status?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Talal Alkhaled is a Computer Engineer from Michigan State University (MSU), specializing in both hardware and software engineering. His focus areas include artificial intelligence, web development, mobile applications, neural network hardware acceleration, and enterprise security implementations. He has completed 11+ major technical projects demonstrating expertise across multiple engineering domains.',
      },
    },
    {
      '@type': 'Question',
      name: 'What services and expertise does Talal Alkhaled offer for projects?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Talal offers comprehensive development services including: React/TypeScript web development with enterprise security (A+ ratings), Flutter mobile app development with AI integration, OpenAI API implementation and chatbot development, web security optimization achieving Mozilla Observatory 110/100 scores, performance optimization (99 Lighthouse scores), hardware design using FPGA/Verilog, NLP API development, and full-stack application architecture. Available for freelance projects, internships, and technical consulting.',
      },
    },
    {
      '@type': 'Question',
      name: 'How can I contact Talal Alkhaled for collaboration or job opportunities?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Contact Talal through his website contact form at talkhaled.com/contact, email at contact@talkhaled.com, or connect via LinkedIn (linkedin.com/in/talal-alkhaled-a0779b321/) and GitHub (github.com/tkkalkhaled). He's available for freelance projects, internship opportunities, full-time positions, and technical collaboration. Response time is typically within 24 hours for professional inquiries.",
      },
    },
    {
      '@type': 'Question',
      name: "What are Talal Alkhaled's most significant technical achievements?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Talal's major achievements include: A+ security ratings (110/100 Mozilla Observatory, top 0.1% globally), 99 desktop/93 mobile Lighthouse performance scores, AI-powered chat system with the latest OpenAI GPT model integration, NOTEorious mobile app with custom OCR and handwriting recognition, neural network hardware accelerator achieving 100x speedup, comprehensive NLP API handling 10,000+ requests/minute, interactive terminal system with realistic human typing simulation, and enterprise-grade web applications with advanced security implementations.",
      },
    },
    {
      '@type': 'Question',
      name: "What makes Talal Alkhaled's projects unique and innovative?",
      acceptedAnswer: {
        '@type': 'Answer',
        text: "Talal's projects demonstrate exceptional technical depth combining multiple advanced technologies: AI integration with real-world applications, custom hardware acceleration for neural networks, enterprise-grade security implementations, performance optimization achieving top-tier metrics, innovative user experiences (like realistic typing simulation), and comprehensive full-stack development spanning web, mobile, hardware, and AI domains. Each project showcases production-ready quality with detailed documentation and live demonstrations.",
      },
    },
    {
      '@type': 'Question',
      name: 'What AI and machine learning projects has Talal Alkhaled developed?',
      acceptedAnswer: {
        '@type': 'Answer',
        text: 'Talal has developed several advanced AI projects: AI-powered chat system with the latest OpenAI GPT model integration and real-time responses, LexifAI custom OCR engine with 95%+ accuracy, handwriting recognition system with gesture detection, AI template generator with intelligent content creation, neural network hardware accelerator on FPGA, and comprehensive NLP API with sentiment analysis, entity recognition, and text summarization. These projects demonstrate expertise in both AI software development and hardware acceleration.',
      },
    },
  ],
};

/** Portfolio collection schema, shown on home + projects pages. */
export const projectsCollectionSchema = {
  '@context': 'https://schema.org',
  '@type': 'CreativeWork',
  name: "Talal Alkhaled's Engineering Projects Portfolio",
  description:
    'Comprehensive collection of innovative computer engineering projects spanning AI, web development, mobile applications, hardware design, and security implementations.',
  creator: {
    '@type': 'Person',
    name: 'Talal Alkhaled',
    url: 'https://talkhaled.com/',
    sameAs: [...PERSON_SAME_AS, 'https://www.instagram.com/talalal5aled'],
  },
  hasPart: [
    {
      '@type': 'SoftwareApplication',
      name: 'AI-Powered Projects Website & Interactive Chat System',
      description:
        'Advanced AI-powered portfolio with the latest OpenAI GPT model integration, real-time conversational AI, and intelligent project showcases.',
      applicationCategory: 'WebApplication',
      operatingSystem: 'Web Browser',
      url: 'https://talkhaled.com/project/ai-powered-projects-chat-system',
      programmingLanguage: ['React', 'TypeScript', 'OpenAI API'],
      keywords: 'AI, OpenAI, GPT, React, TypeScript, Conversational AI',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Enterprise-Grade Website Security Implementation',
      description:
        'A+ security implementation achieving 110/100 Mozilla Observatory score with comprehensive security headers and monitoring.',
      applicationCategory: 'SecurityApplication',
      url: 'https://talkhaled.com/project/website-security-implementation',
      programmingLanguage: ['JavaScript', 'Security Headers', 'CSP'],
      keywords: 'Web Security, Mozilla Observatory, CSP, HSTS, Security Headers',
    },
    {
      '@type': 'MobileApplication',
      name: 'NOTEorious - Modern Notes App',
      description:
        'Comprehensive Flutter mobile application with AI chatbot, handwriting recognition, and advanced note-taking capabilities.',
      applicationCategory: 'ProductivityApplication',
      operatingSystem: ['iOS', 'Android'],
      url: 'https://talkhaled.com/project/modern-notes-app',
      programmingLanguage: ['Flutter', 'Dart', 'MongoDB'],
      keywords: 'Flutter, Mobile App, AI, OCR, Handwriting Recognition, Notes',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'LexifAI - Custom AI OCR Chatbot',
      description:
        'Custom OCR engine with 95%+ accuracy featuring conversational AI interface for document analysis.',
      applicationCategory: 'AIApplication',
      url: 'https://talkhaled.com/project/lexifai-ocr-chatbot',
      programmingLanguage: ['AI', 'OCR', 'Computer Vision', 'Flutter'],
      keywords: 'AI, OCR, Computer Vision, Document Analysis, Machine Learning',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Handwriting Recognition System',
      description:
        'Advanced handwriting-to-text conversion with custom gesture detection and real-time processing.',
      applicationCategory: 'AIApplication',
      url: 'https://talkhaled.com/project/handwriting-recognition',
      programmingLanguage: ['Flutter', 'ML', 'OCR'],
      keywords: 'Handwriting Recognition, Machine Learning, Flutter, Gesture Detection',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'AI Template Generator',
      description:
        'OpenAI-powered template selection with dynamic content generation and interactive WebView rendering.',
      applicationCategory: 'AIApplication',
      url: 'https://talkhaled.com/project/ai-template-generator',
      programmingLanguage: ['OpenAI API', 'React Native', 'WebView'],
      keywords: 'AI Template Generation, OpenAI, Dynamic Content, WebView',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Animated Background System',
      description:
        'Custom Flutter animation framework with dynamic floating circles and performance optimization.',
      applicationCategory: 'UIFramework',
      url: 'https://talkhaled.com/project/animated-background-system',
      programmingLanguage: ['Flutter', 'Dart', 'Animation'],
      keywords: 'Flutter Animation, Custom Painter, UI/UX, Performance',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'IP Logging System',
      description: 'Real-time IP logging with Python server architecture and Flutter mobile integration.',
      applicationCategory: 'NetworkingApplication',
      url: 'https://talkhaled.com/project/ip-logging-system',
      programmingLanguage: ['Python', 'Node.js', 'Express', 'Flutter'],
      keywords: 'IP Logging, Networking, Python, Real-time Monitoring',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Neural Network Hardware Accelerator',
      description:
        'Custom FPGA-based neural network accelerator with 100x speedup and ultra-low power consumption.',
      applicationCategory: 'HardwareAccelerator',
      url: 'https://talkhaled.com/project/neural-network-accelerator',
      programmingLanguage: ['VHDL', 'Verilog', 'FPGA'],
      keywords: 'Neural Networks, FPGA, Hardware Acceleration, Verilog, VHDL',
    },
    {
      '@type': 'WebAPI',
      name: 'Natural Language Processing API',
      description:
        'Comprehensive NLP API with sentiment analysis, entity recognition, and text summarization.',
      applicationCategory: 'AIService',
      url: 'https://talkhaled.com/project/nlp-api',
      programmingLanguage: ['Python', 'Flask', 'NLP', 'Machine Learning'],
      keywords: 'NLP, API, Sentiment Analysis, Text Processing, Machine Learning',
    },
    {
      '@type': 'SoftwareApplication',
      name: 'Interactive Terminal System',
      description:
        'Sophisticated terminal simulation with realistic human typing patterns and Unix-style command processing.',
      applicationCategory: 'DeveloperTool',
      url: 'https://talkhaled.com/project/interactive-terminal-system',
      programmingLanguage: ['React', 'TypeScript', 'Terminal Simulation'],
      keywords: 'Terminal Simulation, Interactive UI, React, TypeScript, Animation',
    },
  ],
  keywords:
    'Computer Engineering Projects, AI Applications, Web Development, Mobile Apps, Hardware Design, Security Implementation',
  url: 'https://talkhaled.com/projects',
};

/** Showcase video schema, shown on home + projects pages. */
export const videoSchema = {
  '@context': 'https://schema.org',
  '@type': 'VideoObject',
  name: 'NOTEorious App Development Showcase - Complete Engineering Process',
  description:
    'Comprehensive development walkthrough of the NOTEorious mobile application featuring AI chatbot integration, handwriting recognition, and advanced note-taking capabilities. Demonstrates full-stack development process from concept to deployment.',
  thumbnailUrl: 'https://talkhaled.com/optimized/NOTEorious-md.webp',
  uploadDate: '2024-12-01T00:00:00Z',
  duration: 'PT3M45S',
  contentUrl: 'https://talkhaled.com/RecentWork.mp4',
  embedUrl: 'https://talkhaled.com/',
  publisher: {
    '@type': 'Person',
    name: 'Talal Alkhaled',
    url: 'https://talkhaled.com',
    sameAs: PERSON_SAME_AS,
  },
  creator: { '@type': 'Person', name: 'Talal Alkhaled', url: 'https://talkhaled.com' },
  inLanguage: 'en-US',
  isFamilyFriendly: true,
  keywords: [
    'mobile app development',
    'Flutter',
    'React',
    'AI integration',
    'NOTEorious',
    'Computer Engineering',
    'Michigan State University',
    'handwriting recognition',
    'OCR technology',
  ],
  genre: ['Technology', 'Education', 'Software Development'],
  educationalLevel: 'Advanced',
  learningResourceType: 'Demonstration',
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://talkhaled.com/' },
  about: [
    { '@type': 'Thing', name: 'Mobile App Development' },
    { '@type': 'Thing', name: 'Flutter Framework' },
    { '@type': 'Thing', name: 'AI Integration' },
  ],
};

/** Blog index schema. */
export const blogSchema = {
  '@context': 'https://schema.org',
  '@type': 'Blog',
  name: "Talal Alkhaled's Advanced Technology Blog",
  alternateName: 'Elite Engineering Insights by Talal Al-Khaled',
  url: 'https://talkhaled.com/blog',
  description:
    'Comprehensive technical blog featuring in-depth articles on AI integration, web security implementations, performance optimization, mobile app development, and cutting-edge engineering solutions. Written by Computer Engineer Talal Alkhaled.',
  inLanguage: 'en-US',
  author: {
    '@type': 'Person',
    name: 'Talal Alkhaled',
    url: 'https://talkhaled.com/',
    sameAs: PERSON_SAME_AS,
  },
  publisher: {
    '@type': 'Organization',
    name: 'Talal Alkhaled',
    url: 'https://talkhaled.com/',
    logo: {
      '@type': 'ImageObject',
      url: 'https://talkhaled.com/optimized/Icon.webp',
      width: 192,
      height: 192,
    },
  },
  about: [
    { '@type': 'Thing', name: 'Artificial Intelligence Integration' },
    { '@type': 'Thing', name: 'Web Security Implementation' },
    { '@type': 'Thing', name: 'Performance Optimization' },
    { '@type': 'Thing', name: 'Mobile App Development' },
    { '@type': 'Thing', name: 'Computer Engineering' },
    { '@type': 'Thing', name: 'React Development' },
    { '@type': 'Thing', name: 'Flutter Development' },
  ],
  // Titles + dates mirror the blogPosts.* locale data (the on-page source of truth)
  blogPost: [
    {
      '@type': 'BlogPosting',
      headline: 'I Built a Fully Production-Ready Developer Portfolio for $1',
      url: 'https://talkhaled.com/blog/production-portfolio-dollar',
      datePublished: '2025-07-02',
      author: { '@type': 'Person', name: 'Talal Alkhaled' },
    },
    {
      '@type': 'BlogPosting',
      headline: 'Optimizing React Performance: 99/100 Lighthouse Score Achievement',
      url: 'https://talkhaled.com/blog/react-performance-optimization',
      datePublished: '2025-06-16',
      author: { '@type': 'Person', name: 'Talal Alkhaled' },
    },
    {
      '@type': 'BlogPosting',
      headline: 'Advanced Web Security Implementation: A Complete Developer Guide',
      url: 'https://talkhaled.com/blog/web-security-implementation',
      datePublished: '2025-06-09',
      author: { '@type': 'Person', name: 'Talal Alkhaled' },
    },
    {
      '@type': 'BlogPosting',
      headline: 'Complete AI Integration Guide: From Concept to Production',
      url: 'https://talkhaled.com/blog/ai-integration-guide',
      datePublished: '2025-06-02',
      author: { '@type': 'Person', name: 'Talal Alkhaled' },
    },
    {
      '@type': 'BlogPosting',
      headline: 'NOTEorious App: Advanced Note-Taking with AI & OCR Technology',
      url: 'https://talkhaled.com/blog/noteorious-app-development',
      datePublished: '2025-05-18',
      author: { '@type': 'Person', name: 'Talal Alkhaled' },
    },
    {
      '@type': 'BlogPosting',
      headline: 'Complete Firebase Cloud Setup: Authentication, Firestore & Beyond',
      url: 'https://talkhaled.com/blog/firebase-cloud-setup',
      datePublished: '2024-12-26',
      author: { '@type': 'Person', name: 'Talal Alkhaled' },
    },
  ],
  keywords:
    'AI integration, web security, performance optimization, React development, Flutter development, mobile apps, computer engineering, technology blog, programming tutorials',
  mainEntityOfPage: { '@type': 'WebPage', '@id': 'https://talkhaled.com/blog' },
};

/** Breadcrumb trail derived from the current page context. */
export function breadcrumbSchema(pageType: PageType, articleData?: ArticleData, projectData?: ProjectData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://talkhaled.com/' },
      ...(pageType && pageType !== 'home'
        ? [
            {
              '@type': 'ListItem',
              position: 2,
              name:
                pageType === 'blog'
                  ? 'Technology Blog'
                  : pageType === 'projects'
                    ? 'Engineering Projects'
                    : pageType === 'project-details'
                      ? 'Project Details'
                      : pageType.charAt(0).toUpperCase() + pageType.slice(1),
              item: `https://talkhaled.com/${pageType === 'project-details' ? 'projects' : pageType}`,
            },
          ]
        : []),
      ...(articleData
        ? [
            {
              '@type': 'ListItem',
              position: 3,
              name: articleData.title,
              item: `https://talkhaled.com/blog/${articleData.slug}`,
            },
          ]
        : []),
      ...(projectData
        ? [
            {
              '@type': 'ListItem',
              position: 3,
              name: projectData.title,
              item: `https://talkhaled.com/project/${projectData.slug}`,
            },
          ]
        : []),
    ],
  };
}

/** SoftwareApplication schema for a single project detail page. */
export function individualProjectSchema(projectData: ProjectData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'SoftwareApplication',
    name: projectData.title,
    description: projectData.description,
    applicationCategory:
      projectData.category === 'ml'
        ? 'AIApplication'
        : projectData.category === 'web'
          ? 'WebApplication'
          : projectData.category === 'hardware'
            ? 'HardwareAccelerator'
            : 'SoftwareApplication',
    creator: {
      '@type': 'Person',
      name: 'Talal Alkhaled',
      url: 'https://talkhaled.com/',
      sameAs: PERSON_SAME_AS,
    },
    dateCreated: projectData.completedDate,
    programmingLanguage: projectData.tags,
    keywords: projectData.tags.join(', '),
    url: `https://talkhaled.com/project/${projectData.slug}`,
    image: projectData.image || 'https://talkhaled.com/optimized/Talal-md.webp',
    ...(projectData.demoUrl && projectData.demoUrl !== '#' && { downloadUrl: projectData.demoUrl }),
    ...(projectData.codeUrl && projectData.codeUrl !== '#' && { codeRepository: projectData.codeUrl }),
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://talkhaled.com/project/${projectData.slug}`,
    },
  };
}

/** BlogPosting schema for a single article. */
export function blogPostingSchema(articleData: ArticleData) {
  return {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: articleData.title,
    description: articleData.description,
    datePublished: articleData.publishDate,
    dateModified: articleData.modifiedDate || articleData.publishDate,
    author: {
      '@type': 'Person',
      name: articleData.author,
      url: 'https://talkhaled.com/',
      sameAs: PERSON_SAME_AS,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Talal Alkhaled',
      url: 'https://talkhaled.com/',
      logo: {
        '@type': 'ImageObject',
        url: 'https://talkhaled.com/optimized/Icon.webp',
        width: 192,
        height: 192,
      },
    },
    image: {
      '@type': 'ImageObject',
      url: articleData.image || 'https://talkhaled.com/optimized/Talal-md.webp',
      width: 1200,
      height: 630,
    },
    keywords: articleData.keywords,
    wordCount: articleData.description.split(' ').length * 5, // Estimate
    timeRequired: `PT${articleData.readingTime}`,
    inLanguage: 'en-US',
    url: `https://talkhaled.com/blog/${articleData.slug}`,
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://talkhaled.com/blog/${articleData.slug}`,
    },
    about: [
      { '@type': 'Thing', name: 'Web Development' },
      { '@type': 'Thing', name: 'Computer Engineering' },
      { '@type': 'Thing', name: 'Technology' },
    ],
    articleSection: 'Technology Blog',
    isPartOf: { '@type': 'Blog', '@id': 'https://talkhaled.com/blog' },
    educationalLevel: 'Advanced',
    learningResourceType: 'Tutorial',
  };
}
